import React, { Activity, useMemo, type FC } from 'react';
import type { TextStyle, ViewStyle } from 'react-native';

import { Stack } from '@/components/layout';
import type { DateTimePickerProps } from '@/components/ui/datetime-picker';
import { DateTimePicker } from '@/components/ui/datetime-picker';
import { Text, TextProps } from '@/components/ui/text';
import { createStyles } from '@/hooks';
import { colors } from '@/theme/colors';

export type FormDateInputProps = {
  label: string;
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
  containerStyle?: ViewStyle | ViewStyle[];
  triggerStyle?: ViewStyle | ViewStyle[];
  labelStyle?: TextStyle | TextStyle[];
  labelProps?: Omit<TextProps, 'children' | 'style'>;
  minimumDate?: Date;
  maximumDate?: Date;
  errorMessage?: string;
} & Omit<
  DateTimePickerProps,
  'placeholder' | 'disabled' | 'style' | 'triggerStyle' | 'pickerStyle'
>;

export const FormDateInput: FC<FormDateInputProps> = ({
  label,
  required = true,
  disabled = false,
  placeholder,
  containerStyle,
  triggerStyle,
  labelStyle,
  minimumDate,
  maximumDate,
  labelProps,
  errorMessage,
  ...datePickerProps
}) => {
  const displayLabel = useMemo(() => label?.toUpperCase?.() ?? '', [label]);
  const resolvedPlaceholder = placeholder ?? label;
  const styles = useStyles();

  return (
    <Stack direction="column" gap={8} width="100%" style={containerStyle}>
      <Stack direction="row" align="center" justify="flex-start">
        <Text
          variant="Font Inter/Subtitle/Regular 11px"
          style={[styles.label, labelStyle]}
          {...labelProps}>
          {displayLabel}
        </Text>
        {required ? (
          <Text variant="Font Inter/Subtitle/Regular 11px" style={styles.requiredMark}>
            *
          </Text>
        ) : null}
      </Stack>

      <DateTimePicker
        {...datePickerProps}
        disabled={disabled}
        placeholder={resolvedPlaceholder}
        triggerStyle={triggerStyle}
        minimumDate={minimumDate ?? new Date('1875-01-01')}
        maximumDate={maximumDate}
      />

      <Activity mode={errorMessage ? 'visible' : 'hidden'}>
        <Text
          variant="Font Inter/Subtitle/Regular 11px"
          style={[styles.helperText, { color: colors.Error }]}>
          {errorMessage}
        </Text>
      </Activity>
    </Stack>
  );
};

const useStyles = createStyles(({ scale }) => ({
  helperText: {
    marginTop: -2,
  },
  label: {
    color: colors['Labels/Primary'],
  },
  requiredMark: {
    marginLeft: scale(4),
    color: colors.Red,
  },
}));

export default FormDateInput;
